import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/** Max viewport width (px) for swipe-down-to-close on the cart drawer — matches sidebar mobile breakpoint. */
const SWIPE_MAX_WIDTH = 1023.98;
const SWIPE_DISMISS_THRESHOLD = 0.25;
const SWIPE_VELOCITY_THRESHOLD = 0.5;

/**
 * Panel transition used when releasing a swipe. Mirrors the custom sidebar's
 * stylesheet transition (`--transition-speed` / `--transition-easing`, i.e. 0.2s
 * ease) rather than the faster open/close keyframe speed (`--animation-speed`),
 * so the drawer glides shut exactly like the non-dialog sidebars.
 */
const SWIPE_RELEASE_TRANSITION = 'transform var(--transition-speed) var(--transition-easing)';

/**
 * Added to the dialog while a swipe is dismissing it. It stretches the dialog's
 * keep-alive keyframe and the scrim fade to `--transition-speed` so they finish
 * together with the panel's `SWIPE_RELEASE_TRANSITION` — without it `closeDialog`
 * would hide the dialog at the faster `--animation-speed`, cutting the slide short.
 */
const SWIPE_CLOSING_CLASS = 'cart-drawer__dialog--swipe-closing';

/**
 * Walks up from the touch target to the panel looking for a scrollable ancestor
 * that is currently scrolled away from the top. When found, a downward swipe
 * should scroll that content rather than dismiss the drawer — so the drag is only
 * armed when this returns null (mirrors the non-dialog sidebar behavior).
 * @param {EventTarget | null} target
 * @param {HTMLElement} panel
 * @returns {HTMLElement | null}
 */
function getScrollableParent(target, panel) {
    let node = target instanceof HTMLElement ? target : null;
    while (node && node !== panel) {
        if (node.scrollHeight > node.clientHeight && node.scrollTop > 0) return node;
        node = node.parentElement;
    }
    return null;
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [panel] - The sidebar panel inside the dialog.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
    /** @type {number} */
    #summaryThreshold = 0.5;

    /** @type {AbortController | null} */
    #historyAbortController = null;

    /** @type {AbortController | null} */
    #swipeAbortController = null;

    /** @type {number} */
    #dragStartY = 0;
    /** @type {number} */
    #dragCurrentY = 0;
    /** @type {boolean} */
    #dragging = false;
    /** @type {boolean} */
    #dragCandidate = false;
    /** @type {number} */
    #dragStartTime = 0;

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
        this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
        this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);

        if (history.state?.cartDrawerOpen) {
            history.replaceState(null, '');
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
        this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
        this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
        this.#historyAbortController?.abort();
        this.#swipeAbortController?.abort();
    }

    #handleOpen = () => {
        this.#updateStickyState();
        this.#handleHistoryOpen();
        this.#bindSwipe();
        window.initCartUpsellSliders?.(this, { defer: true });
    };

    #handleClose = () => {
        this.#handleHistoryClose();
        this.#unbindSwipe();
        this.#resetSwipeTransform();
    };

    #handleHistoryOpen = () => {
        if (!isMobileBreakpoint()) return;

        if (!history.state?.cartDrawerOpen) {
            history.pushState({ cartDrawerOpen: true }, '');
        }

        this.#historyAbortController = new AbortController();
        window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
    };

    #handleHistoryClose = () => {
        this.#historyAbortController?.abort();
        if (history.state?.cartDrawerOpen) {
            history.back();
        }
    };

    #handlePopState = async () => {
        if (this.refs.dialog?.open) {
            this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
            await this.closeDialog();
            this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
        }
    };

    /**
     * Handles cart add events - opens drawer if auto-open and announces count when open.
     * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { source?: string, didError?: boolean } }>} event
     */
    #handleCartAdd = (event) => {
        const isAddToCart =
            event.detail?.data?.source === 'product-form-component' && !event.detail?.data?.didError;

        if (this.hasAttribute('auto-open') && isAddToCart) {
            this.showDialog();
        }

        this.#announceCartCount(event.detail.resource?.item_count);
    };

    /**
     * Announces cart count to screen readers when dialog is open.
     * @param {number | undefined} cartCount
     */
    #announceCartCount(cartCount) {
        const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
        if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

        liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
    }

    open() {
        this.showDialog();

        /**
         * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
         */
        customElements.whenDefined('shopify-payment-terms').then(() => {
            const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
            const cta = installmentsContent?.querySelector('#shopify-installments-cta');
            cta?.addEventListener('click', this.closeDialog, { once: true });
        });
    }

    close() {
        this.closeDialog();
    }

    updateSummaryHeight() {
        requestAnimationFrame(() => {
            this.#updateStickyState();
        });
    }

    #updateStickyState() {
        const { dialog } = /** @type {Refs} */ (this.refs);
        if (!dialog) return;

        // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
        const content = dialog.querySelector('.cart-drawer__content');
        const summary = dialog.querySelector('.cart-drawer__summary');

        if (!content || !summary) {
            // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
            dialog.setAttribute('cart-summary-sticky', 'false');
            this.style.removeProperty('--cart-drawer-summary-height');
            return;
        }

        const drawerHeight = dialog.getBoundingClientRect().height;
        const summaryHeight = summary.getBoundingClientRect().height;
        const ratio = summaryHeight / drawerHeight;
        this.style.setProperty('--cart-drawer-summary-height', `${summaryHeight}px`);
        dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
    }

    #isSwipeViewport() {
        return window.matchMedia(`(max-width: ${SWIPE_MAX_WIDTH}px)`).matches;
    }

    /**
     * Binds the drag-to-close gesture. `touchstart` is bound to the whole panel so
     * a dismiss drag can begin from anywhere on it (like the non-dialog sidebars),
     * except that a swipe starting inside a scrolled scrollable region scrolls that
     * content instead (see `#onDragStart` / `getScrollableParent`). `touchmove` /
     * `touchend` are tracked on the window so the gesture keeps following the finger
     * even if it leaves the panel.
     */
    #bindSwipe() {
        const { panel } = /** @type {Refs} */ (this.refs);
        if (!(panel instanceof HTMLElement)) return;

        this.#swipeAbortController?.abort();
        this.#swipeAbortController = new AbortController();
        const { signal } = this.#swipeAbortController;

        panel.addEventListener('touchstart', this.#onDragStart, { signal, passive: true });
        window.addEventListener('touchmove', this.#onDragMove, { signal, passive: false });
        window.addEventListener('touchend', this.#onDragEnd, { signal });
        window.addEventListener('touchcancel', this.#onDragEnd, { signal });
    }

    #unbindSwipe() {
        this.#swipeAbortController?.abort();
        this.#swipeAbortController = null;
    }

    #resetSwipeTransform() {
        const { dialog, panel } = /** @type {Refs} */ (this.refs);
        dialog?.classList.remove(SWIPE_CLOSING_CLASS);
        if (panel instanceof HTMLElement) {
            // Restore the stylesheet-driven open/close animation for the next open.
            panel.style.animation = '';
            panel.style.transition = '';
            panel.style.transform = '';
        }
        this.#dragging = false;
        this.#dragCandidate = false;
    }

    #onDragStart = (/** @type {TouchEvent} */ event) => {
        const { dialog, panel } = /** @type {Refs} */ (this.refs);
        this.#dragging = false;
        this.#dragCandidate = false;
        if (!dialog?.open || !(panel instanceof HTMLElement) || !this.#isSwipeViewport()) return;

        const touch = event.touches[0];
        if (!touch) return;

        // A swipe that begins inside a scrolled scrollable region must scroll that
        // content, not dismiss the drawer — so don't even arm the gesture there.
        if (getScrollableParent(event.target, panel)) return;

        this.#dragStartY = touch.clientY;
        this.#dragCurrentY = touch.clientY;
        this.#dragStartTime = Date.now();
        this.#dragCandidate = true;
    };

    #onDragMove = (/** @type {TouchEvent} */ event) => {
        const { panel } = /** @type {Refs} */ (this.refs);
        if (!(panel instanceof HTMLElement)) return;

        const touch = event.touches[0];
        if (!touch) return;

        // Wait for a downward move before committing, so an upward swipe (or a tap)
        // is left to the browser (e.g. scrolling a not-yet-scrolled list upward).
        if (!this.#dragging) {
            if (!this.#dragCandidate || touch.clientY - this.#dragStartY <= 0) return;
            this.#dragging = true;
            // The `[open] .sidebar` keyframe animation runs `forwards`, so it holds
            // `translateY(0)` and would override our inline transform. Disable it for
            // the duration of the drag so the panel follows the finger. The base
            // `.sidebar` rule is `translateY(100%)` (off-screen), so pin the transform
            // to `translateY(0)` in the same frame to avoid a flash to the base value.
            panel.style.transition = 'none';
            panel.style.transform = 'translateY(0px)';
            panel.style.animation = 'none';
        }

        this.#dragCurrentY = touch.clientY;
        // Only translate downward — the panel hides toward the bottom of the screen.
        const clampedDelta = Math.max(0, this.#dragCurrentY - this.#dragStartY);
        panel.style.transform = `translateY(${clampedDelta}px)`;
        event.preventDefault();
    };

    #onDragEnd = () => {
        const { dialog, panel } = /** @type {Refs} */ (this.refs);
        const wasDragging = this.#dragging;
        this.#dragging = false;
        this.#dragCandidate = false;
        if (!wasDragging || !(panel instanceof HTMLElement)) return;

        const deltaY = this.#dragCurrentY - this.#dragStartY;
        const elapsed = Date.now() - this.#dragStartTime;
        const velocity = elapsed > 0 ? deltaY / elapsed : 0;
        const height = panel.offsetHeight || 1;
        const shouldDismiss =
            deltaY > height * SWIPE_DISMISS_THRESHOLD || velocity > SWIPE_VELOCITY_THRESHOLD;

        // Animation is still disabled (set when the drag committed), so the inline
        // transform/transition own the panel until `#resetSwipeTransform` restores the
        // keyframes. Match the custom sidebar's slower, eased glide instead of the
        // snappier open/close keyframe speed.
        panel.style.transition = SWIPE_RELEASE_TRANSITION;

        if (shouldDismiss) {
            // Keep the dialog lifecycle + scrim fade in step with the slower panel
            // glide, then slide the panel the rest of the way down as the scrim fades.
            dialog?.classList.add(SWIPE_CLOSING_CLASS);
            panel.style.transform = 'translateY(100%)';
            void this.closeDialog();
        } else {
            // Snap back to the resting position.
            panel.style.transform = 'translateY(0)';
        }
    };
}

if (!customElements.get('cart-drawer-component')) {
    customElements.define('cart-drawer-component', CartDrawerComponent);
}
